import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isPortalSelfEmbedUrl } from '@/utils/iframeUrl';

export interface ProductFormValues {
  name: string;
  description: string;
  iframeUrl: string;
  openInNewTab: boolean;
}

interface ProductFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ProductFormValues;
  submitError?: string | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: ProductFormValues) => Promise<void>;
}

export function ProductFormDialog({
  open,
  mode,
  initialValues,
  submitError,
  isSubmitting,
  onClose,
  onSubmit,
}: ProductFormDialogProps) {
  const { t } = useTranslation('entity-portal');

  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup
            .string()
            .trim()
            .required(t('product.validationProductNameRequired')),
          description: yup.string().default(''),
          iframeUrl: yup
            .string()
            .trim()
            .required(t('product.validationProductIframeRequired'))
            .url(t('product.validationProductIframeInvalid'))
            .test('not-portal-self-embed', t('product.validationIframeSelfEmbed'), (value) =>
              value ? !isPortalSelfEmbedUrl(value) : true,
            ),
          openInNewTab: yup.boolean().default(false),
        })
        .required(),
    [t],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<ProductFormValues>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (open) {
      reset(initialValues);
    }
  }, [initialValues, open, reset]);

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {mode === 'create' ? t('product.createProduct') : t('product.editProduct')}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {submitError ? (
            <Alert severity="error">{submitError}</Alert>
          ) : undefined}
          <TextField
            label={t('product.fieldName')}
            required
            disabled={isSubmitting}
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />
          <TextField
            label={t('product.fieldDescription')}
            multiline
            minRows={2}
            disabled={isSubmitting}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
            {...register('description')}
          />
          <TextField
            label={t('product.fieldIframeUrl')}
            placeholder={t('product.fieldIframePlaceholder')}
            disabled={isSubmitting}
            error={Boolean(errors.iframeUrl)}
            helperText={errors.iframeUrl?.message}
            {...register('iframeUrl')}
          />
          <Controller
            name="openInNewTab"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={t('product.fieldOpenInNewTab')}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            void handleSubmit(onSubmit)();
          }}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? mode === 'create'
              ? t('common.creating')
              : t('common.saving')
            : mode === 'create'
              ? t('common.create')
              : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

